import type { ProcessingJob } from "features/dt-plus-setup/model/types";

import { Table } from "components";
import { detailsCellHeader } from "components/DetailsCellHeader";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { formatProgressPercent, getLastStepMessage } from "features/dt-plus-setup/lib/executionSteps";
import { ExecutionStatusChip } from "features/shared/components/ExecutionStatusChip";
import { formatDurationToMinutesAndSeconds } from "features/shared/lib/formatters";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { convertDatetimeStringToDate } from "utils/date";
import { valueOrAlternative } from "utils/namesManipulation";

import { useTransientProcessingJobs } from "../hooks/useTransientProcessingJobs";

type TransientRequestHistoryTableProps = {
  onJobSelect: (job: ProcessingJob) => void;
};

export const TransientRequestHistoryTable = ({ onJobSelect }: TransientRequestHistoryTableProps) => {
  const { t } = useTranslation();
  const { isLoading, jobs } = useTransientProcessingJobs();
  const headers = useMemo(
    () => [
      {
        dataKey: "user_email",
        label: t("general.user"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => valueOrAlternative(row.user?.email),
      },
      {
        dataKey: "status",
        label: t("Execution.transient.requestHistory.columns.status"),
        valueTransformation: (row: ProcessingJob) => <ExecutionStatusChip status={row.status} />,
      },
      {
        dataKey: "progress_percent",
        label: t("general.progress"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => formatProgressPercent(row.progress_percent),
      },
      {
        dataKey: "current_step_name",
        label: t("Execution.history.columns.currentStep"),
        minWidth: "12rem",
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => valueOrAlternative(row.current_step_name),
      },
      {
        dataKey: "executed_at",
        label: t("general.executedAt"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => convertDatetimeStringToDate(row.executed_at),
      },
      {
        dataKey: "duration_seconds",
        label: t("general.duration"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => formatDurationToMinutesAndSeconds(row.duration_seconds),
      },
      {
        dataKey: "last_step_message",
        label: t("general.summary"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => (
          <div className="max-w-[30rem] truncate">{valueOrAlternative(getLastStepMessage(row.steps))}</div>
        ),
      },
      detailsCellHeader<ProcessingJob>({}, onJobSelect),
    ],
    [onJobSelect, t]
  );

  return (
    <LoadingOrEmptyWrapper
      emptyComponent={() => (
        <p className="py-10 text-center text-lds-foreground-muted font-ds-body-regular-md">
          {t("Execution.transient.requestHistory.empty")}
        </p>
      )}
      height="20rem"
      showEmpty={!isLoading && jobs.length === 0}
      showLoading={isLoading}
    >
      <Table
        data={jobs}
        editable={() => false}
        headers={headers}
        idColumnName="job_id"
        onRowClick={onJobSelect}
        rowRemoveable={() => false}
      />
    </LoadingOrEmptyWrapper>
  );
};
